import { useMutation, useQuery, useQueryClient } from 'react-query';
import { queryKey } from 'constants/queryKey';
import { LinkShortenerApi } from 'api/linkShortener';
import { useToast } from 'app/hooks/useToast';
import { useTranslation } from 'react-i18next';
import { copyTextToClipboard } from '@kontentino/kontentino-utils/clipboard';
import Popup from 'utils/popup';
import { useState } from 'react';
import { LinkSource, ShortenLink } from 'types/LinkShortener';

type Props = {
  pageId?: number;
};

export function useLinkShortenerContainer(props: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isLoading, data } = useQuery([queryKey.shortenLinks], () =>
    LinkShortenerApi.allLinks({
      pageIds: props.pageId ? [String(props.pageId)] : null,
    }),
  );
  const deleteLink = useMutation(LinkShortenerApi.delete, {
    onSuccess() {
      queryClient.invalidateQueries([queryKey.shortenLinks]);
      toast(t('linkShortener.successfullyRemoved'));
    },
  });
  const [filter, setFilter] = useState('');
  const [hashtagToEdit, setShortenLinkToEdit] = useState<
    null | undefined | ShortenLink
  >();
  const [linkStatsToSee, setLinkStatsToSee] = useState<
    { id: string; source: LinkSource } | undefined
  >();

  async function copyToClipboard(hashtags: string) {
    try {
      await copyTextToClipboard(hashtags);
      toast(t('copiedToClipboard'));
    } catch (e) {
      toast(t('somethingWentWrong'), 'error');
    }
  }

  async function remove(linkId: string, source: LinkSource) {
    const { isConfirmed } = await Popup.confirm();

    if (isConfirmed) {
      deleteLink.mutate({ id: linkId, source });
    }
  }

  return {
    isLoading,
    shortenLinks: data,
    copyToClipboard,
    remove,
    hashtagToEdit,
    setShortenLinkToEdit,
    linkStatsToSee,
    setLinkStatsToSee,
    filter,
    setFilter,
  };
}
